import { Info, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import type { PromoteSurfaceAsset } from "../api/types";
import { dispositionLabel, formatConfidencePercent } from "../utils/disposition";

type Props = {
  asset: PromoteSurfaceAsset;
  className?: string;
  /**
   * `compact` — card grid chips.
   * `overlay` — glass card over the preview (mirrors observations placement).
   * `action` — compact strip (legacy footer placement).
   * `detail` — bordered panel in a side column.
   */
  variant?: "compact" | "overlay" | "action" | "detail";
  /** @deprecated Prefer `variant="compact"`. */
  compact?: boolean;
};

/**
 * Surfaces the AI disposition proposal so review is confirm/override, not blank tagging.
 */
export default function PromoteSurfaceAiProposal({
  asset,
  className,
  variant,
  compact,
}: Props) {
  const mode = variant ?? (compact ? "compact" : "detail");
  const disposition = dispositionLabel(asset.recommended_disposition);
  const confidence = formatConfidencePercent(asset.overall_confidence);
  const rationale = asset.disposition_rationale?.trim() || null;

  if (!disposition && !confidence && !rationale) return null;

  if (mode === "compact") {
    // Thumb: disposition only — confidence belongs in tooltip / detail surfaces.
    if (!disposition && !rationale) return null;

    const summary = disposition ? `AI · ${disposition}` : "AI";

    const chipBody = (
      <>
        <span className="truncate py-1 pl-2 font-semibold uppercase tracking-wide">
          {summary}
        </span>
        {rationale && (
          <span
            className="mr-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-white/85"
            aria-hidden
          >
            <Info className="h-3.5 w-3.5" strokeWidth={2.5} />
          </span>
        )}
      </>
    );

    if (!rationale) {
      return (
        <div className={cn("min-w-0", className)}>
          <span className="inline-flex max-w-full items-center rounded-md bg-black/70 pr-2 text-xs leading-none text-white shadow-sm backdrop-blur-[2px]">
            {chipBody}
          </span>
        </div>
      );
    }

    return (
      <div className={cn("min-w-0", className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className={cn(
                "pointer-events-auto inline-flex max-w-full items-center rounded-md bg-black/70 text-left text-xs leading-none text-white shadow-sm backdrop-blur-[2px]",
                "transition hover:bg-black/80",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              )}
              aria-label={
                confidence
                  ? `AI recommendation: ${summary}, ${confidence} confidence. ${rationale}`
                  : `AI recommendation: ${summary}. ${rationale}`
              }
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {chipBody}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="start"
            sideOffset={6}
            className="z-[80] max-w-[min(16rem,calc(100vw-2rem))] space-y-1.5 px-3 py-2 text-left text-balance"
          >
            {(disposition || confidence) && (
              <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs font-semibold">
                {disposition && <span>Recommend {disposition}</span>}
                {confidence && (
                  <span className="tabular-nums text-muted-foreground">
                    {confidence} confidence
                  </span>
                )}
              </p>
            )}
            <p className="text-xs leading-relaxed text-popover-foreground/90">{rationale}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  if (mode === "overlay") {
    const toneBorder =
      asset.recommended_disposition === "accept"
        ? "border-sky-400/25"
        : asset.recommended_disposition === "reject"
          ? "border-rose-400/25"
          : "border-white/15";
    const toneBg =
      asset.recommended_disposition === "accept"
        ? "from-sky-700/90 to-blue-800/90 shadow-sky-950/30"
        : asset.recommended_disposition === "reject"
          ? "from-rose-700/90 to-red-800/90 shadow-rose-950/30"
          : "from-neutral-800/90 to-neutral-900/90 shadow-black/40";

    return (
      <div
        className={cn(
          "rounded-xl border bg-gradient-to-br p-3 shadow-2xl backdrop-blur-md",
          toneBorder,
          toneBg,
          className
        )}
        aria-label="AI recommendation"
        role="status"
      >
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-white/15">
              <Sparkles className="h-3 w-3 text-white" aria-hidden />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">
              AI suggestion
            </span>
          </div>
          <div className="flex shrink-0 items-baseline gap-1.5">
            {disposition && (
              <span className="text-sm font-extrabold uppercase leading-none tracking-wide text-white">
                {disposition}
              </span>
            )}
            {confidence && (
              <span className="text-xs font-semibold leading-none tabular-nums text-white/85">
                {confidence} confidence
              </span>
            )}
          </div>
        </div>
        {rationale && (
          <p className="text-xs leading-relaxed text-white/80">{rationale}</p>
        )}
      </div>
    );
  }

  if (mode === "action") {
    const dispositionTone =
      asset.recommended_disposition === "accept"
        ? "border-primary/40 bg-primary/15 text-foreground"
        : asset.recommended_disposition === "reject"
          ? "border-destructive/40 bg-destructive/15 text-foreground"
          : "border-foreground/20 bg-muted text-foreground";

    return (
      <div
        className={cn(
          "flex items-start gap-2 rounded-md border px-2.5 py-1.5",
          dispositionTone,
          className
        )}
        aria-label="AI recommendation"
        role="status"
        title={rationale ?? undefined}
      >
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm leading-tight">
            {disposition && (
              <span className="font-bold tracking-tight">
                Suggests {disposition}
              </span>
            )}
            {confidence && (
              <span className="text-xs font-semibold tabular-nums opacity-80">
                {confidence} confidence
              </span>
            )}
          </p>
          {rationale && (
            <p className="mt-0.5 truncate text-xs leading-snug opacity-90">{rationale}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <section
      className={cn("rounded-lg border border-border bg-muted/40 p-3", className)}
      aria-label="AI recommendation"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        AI recommendation
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {disposition && (
          <span className="rounded-md bg-primary/15 px-2 py-0.5 text-sm font-semibold text-foreground">
            Recommend {disposition}
          </span>
        )}
        {confidence && (
          <span className="text-sm tabular-nums text-muted-foreground">{confidence} confidence</span>
        )}
      </div>
      {rationale && (
        <p className="mt-2 text-sm leading-relaxed text-foreground/90">{rationale}</p>
      )}
    </section>
  );
}
